import React from 'react';
import { observer } from 'mobx-react-lite';
import { useFullFormPageStore } from 'store/pages/quote/add-employees/full-form/pageStore';
import { DynamicFormModal } from '@insurancemenu/imenu-components-lib';
import { toJS, action } from 'mobx';
import { DependentsChoice, ProductLineInterestChange } from 'types/electionService';

import SubscriberInterestCard from 'components/common/election/cards/SubscriberInterestCard';
import { PRODUCT_LINE_RATE_MODAL_TEST_ID, ELECTIONS_TAB_TEST_ID } from '../../constants';

const ElectionsTab: React.FC = observer(() => {
  const { electionsModule } = useFullFormPageStore();

  if (electionsModule.subscribers.length === 0) {
    throw new Error('Cannot render ElectionsTab without a subscriber delegate');
  }

  const subscriberAdapter = electionsModule.subscribers[0];
  const subscriberDelegate = subscriberAdapter?.subscriberDelegate;
  if (!subscriberDelegate) {
    throw new Error('Cannot render ElectionsTab without a subscriber delegate');
  }

  const pLineRateModalDelegate = subscriberDelegate.productLineRateModalDelegate;

  /**
   * Even with this re-references, mobx will throw one gazillion warnings because the subscriberAdapter is the observable.
   * The SubscriberCard must be moved from the lib to here and has its own observer wrapper, then we'll be able to use the
   * observables directly and avoid the warnings.
   */
  const subscriber = subscriberDelegate.subscriber;

  const domainProductLineIds = electionsModule.domainProductLineIds.map((productLineId) => toJS(productLineId));
  const requiredSubInfo = toJS(electionsModule.electionPresenter) ?? undefined;

  const onProductLineInterestChange = action((change: ProductLineInterestChange): void => {
    void subscriberAdapter.syncProductLineInterest(change);
  });

  const onDependentsChange = action((dependentsChoice: DependentsChoice): void => {
    void subscriberAdapter.syncDependentsInInterestedProductLines(dependentsChoice);
  });

  const onProductLineAction = action((formId: string): void => {
    void pLineRateModalDelegate.openAndFetchForm(subscriber, formId);
  });

  return (
    <div data-test-id={ELECTIONS_TAB_TEST_ID}>
      <SubscriberInterestCard
        subscriber={subscriberDelegate.subscriberPresenter}
        productLineIds={domainProductLineIds}
        requiredSubInfo={requiredSubInfo}
        callbacksMap={{
          onDependentsChange,
          onProductLineAction,
          onProductLineInterestChange,
        }}
      />
      <DynamicFormModal
        show={pLineRateModalDelegate.isVisible}
        onClose={pLineRateModalDelegate.close}
        formDelegate={pLineRateModalDelegate.dynamicFormDelegate}
        isEditable
        modalMaxWidth="90rem"
        formBodyMinHeight="20rem"
        data-test-id={PRODUCT_LINE_RATE_MODAL_TEST_ID}
      />
    </div>
  );
});

export default ElectionsTab;
